import { showErrorMessage, extractErrorMessage } from '../constants';
import {
  baseUrlNoAuth,
  virtualAccountBaseUrl,
  getPartnerAxiosInstance,
} from './apiClient';

const baseUrlWithAuth = getPartnerAxiosInstance();

export class apiEndPoints {
  static extractError(error) {
    const errorMessage = extractErrorMessage(error);
    const status = error.response ? error.response.status : null;

    // Don't show error message for token expiry/invalidation (401/403)
    // as the app will redirect the user to login page immediately anyway.
    if (errorMessage && status !== 401 && status !== 403) {
      showErrorMessage(errorMessage);
    }
    return errorMessage;
  }

  static async userRegistration(data) {
    try {
      return baseUrlNoAuth.post('/users/register', data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async userSignIn(data) {
    try {
      return baseUrlNoAuth.post('/users/login/token', data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async userSignOut() {
    try {
      return baseUrlWithAuth.post('/users/logout');
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async listFlavours() {
    try {
      return baseUrlWithAuth.get('/openstack/flavors');
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async listPartnerFlavours(data) {
    try {
      return baseUrlWithAuth.get(`/partner/flavors/price-list${data}`);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async listPartnerResources(data) {
    try {
      return baseUrlWithAuth.get(`/partner/resources${data}`);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async listPartnerWallet(data) {
    try {
      return baseUrlWithAuth.get(`/partner/wallet${data}`);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async listPartnerPriceLists() {
    try {
      return baseUrlWithAuth.get(`/partner/resource/price-lists`);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async listUserProjects() {
    try {
      return baseUrlWithAuth.get('/openstack/projects');
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async updateUserProjects(data) {
    try {
      return baseUrlWithAuth.put(`/openstack/projects/${data.projectId}`, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async updateResourceSubMode(resourceId, data) {
    try {
      return baseUrlWithAuth.put(
        `/openstack/update-resource-sub-mode/${resourceId}`,
        data,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createProject(data) {
    try {
      return baseUrlWithAuth.post('openstack/projects/create-project', data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async switchProject(projectId) {
    try {
      return baseUrlWithAuth.get(`openstack/switch-project/${projectId}`);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async listImages() {
    try {
      return baseUrlWithAuth.get('/openstack/images');
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createKeypair(data, projectId) {
    try {
      return baseUrlWithAuth.post(
        `openstack/${projectId}/keypair-create`,
        data,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async listKeypairs(projectId) {
    try {
      return baseUrlWithAuth.get(`/openstack/${projectId}/keypairs`);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createServer(data, projectId) {
    try {
      return baseUrlWithAuth.post(`openstack/${projectId}/server-create`, data);
    } catch (error) {
      throw error;
    }
  }

  static async setPartnerFlavorPrice(data, flavorId) {
    try {
      return baseUrlWithAuth.patch(
        `/partner/flavors/set-flavor-price/${flavorId}`,
        data,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async listServers(projectId) {
    try {
      return baseUrlWithAuth.get(`/openstack/${projectId}/servers`);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async listServersDB(projectId) {
    try {
      return baseUrlWithAuth.get(
        `/openstack/${projectId}/servers?db_servers=yes`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async listFloatingIps(projectId) {
    try {
      return baseUrlWithAuth.get(`/openstack/${projectId}/floating-ips`);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getServer(projectId, serverId) {
    try {
      return baseUrlWithAuth.get(`/openstack/${projectId}/servers/${serverId}`);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getDBServerStatus(projectId, serverId, serverIp) {
    try {
      return baseUrlWithAuth.get(
        `/openstack/${projectId}/servers/${serverId}/server-creation-status?server_ip=${serverIp}`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getMigrationStatus(projectId, taskId) {
    try {
      return baseUrlWithAuth.get(
        `/openstack/${projectId}/servers/${taskId}/migration-status`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async checkDbConnect(projectId, formData) {
    try {
      return baseUrlWithAuth.post(
        `/openstack/${projectId}/check-db-connect`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getPreflightCheckStatus(projectId, taskId) {
    try {
      return baseUrlWithAuth.get(
        `/openstack/${projectId}/preflight-check-status/${taskId}`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getPartners() {
    try {
      return baseUrlWithAuth.get(`/partner/partner-list`);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getPartner(partnerId) {
    try {
      return baseUrlWithAuth.get(`/partner/partners/${partnerId}`);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getFloatingIPs(projectId, serverId) {
    try {
      return baseUrlWithAuth.get(
        `/openstack/${projectId}/floating-ips/${serverId}`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getNetworks(projectId) {
    try {
      return baseUrlWithAuth.get(`/openstack/${projectId}/networks`);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getSubNetworks(projectId) {
    try {
      return baseUrlWithAuth.get(`/openstack/${projectId}/subnets`);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getNetwork(projectId, networkId) {
    try {
      return baseUrlWithAuth.get(
        `/openstack/${projectId}/networks/${networkId}`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getLoadBalancer(projectId, loadBalancerId) {
    try {
      return baseUrlWithAuth.get(
        `/openstack/${projectId}/load-balancers/${loadBalancerId}`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getListener(projectId, listenerId) {
    try {
      return baseUrlWithAuth.get(
        `/openstack/${projectId}/listeners/${listenerId}`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getPool(projectId, poolId) {
    try {
      return baseUrlWithAuth.get(
        `/openstack/${projectId}/load-balancer-pools/${poolId}`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getNetworkSubnet(projectId, data) {
    try {
      return baseUrlWithAuth.get(
        `/openstack/${projectId}/subnets/${data?.subnetId}?=network_id=${data?.networkId}`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getRouter(projectId, routerId) {
    try {
      return baseUrlWithAuth.get(`/openstack/${projectId}/routers/${routerId}`);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async listServerSnapshots(projectId) {
    try {
      return baseUrlWithAuth.get(`/openstack/${projectId}/server-snapshots`);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async listVolumeSnapshots(projectId) {
    try {
      return baseUrlWithAuth.get(`/openstack/${projectId}/snapshots`);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async listVolumeBackups(projectId) {
    try {
      return baseUrlWithAuth.get(`/openstack/${projectId}/backups`);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async listVolumeSnaphot(projectId, snapshotId) {
    try {
      return baseUrlWithAuth.get(
        `/openstack/${projectId}/snapshot/${snapshotId}`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async listLocalImageDetails() {
    try {
      return baseUrlWithAuth.get(`/openstack/local-image-details`);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createSnapshot(data) {
    try {
      return baseUrlWithAuth.post(
        `/openstack/${data.projectId}/servers/snapshots/create-snapshot`,
        data,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createSnapshotForVolume(data) {
    try {
      return baseUrlWithAuth.post(
        `/openstack/${data.projectId}/volume-create`,
        data,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async registerWhiteLabel(formData) {
    try {
      const config = {
        timeout: 30000,
      };

      for (let [key, value] of formData.entries()) {
        console.log(
          `${key}:`,
          value instanceof File
            ? `File: ${value.name} (${value.size} bytes)`
            : value,
        );
      }

      return baseUrlWithAuth.post('/partner/add-partner', formData, config);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createSnapshotVolume(data) {
    try {
      return baseUrlWithAuth.post(
        `/openstack/${data.projectId}/snapshots`,
        data,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createBackupVolume(data) {
    try {
      return baseUrlWithAuth.post(
        `/openstack/${data.projectId}/${data.volumeId}/backup-create`,
        data,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async resizeServer(data) {
    try {
      return baseUrlWithAuth.post(
        `/openstack/${data.projectId}/servers/${data.serverId}/resize`,
        data,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async confirmResizeServer(data) {
    try {
      return baseUrlWithAuth.post(
        `/openstack/${data.projectId}/servers/${data.serverId}/resize/confirm`,
        data,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async stopResizeServer(data) {
    try {
      return baseUrlWithAuth.get(
        `/openstack/${data.projectId}/servers/${data.serverId}/resize/revert`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createVirtualAccount(data) {
    try {
      return virtualAccountBaseUrl.post(`/create_account`, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createSessionVirtualAccount(data) {
    try {
      return baseUrlWithAuth.post(`openstack/credit`, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getVirtualAccount(data) {
    try {
      return baseUrlWithAuth.get(`openstack/credit`, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async validateCredit(data) {
    try {
      return baseUrlWithAuth.get(
        `/openstack/servers/validate-server-price?to_be_provisioned_price=${data}`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createContactSupport(data) {
    try {
      return baseUrlWithAuth.post(`openstack/contact-us`, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createSecurityGroup(data, projectId) {
    try {
      return baseUrlWithAuth.post(
        `openstack/${projectId}/create-security-group`,
        data,
      );
    } catch (error) {
      throw error;
    }
  }

  static async createSecurityGroupRule(data, projectId, securityGroupId) {
    try {
      return baseUrlWithAuth.post(
        `openstack/${projectId}/${securityGroupId}/create-security-group-rule`,
        data,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async listSecurityGroups(projectId) {
    try {
      return baseUrlWithAuth.get(`openstack/${projectId}/security-groups`);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async deleteSecurityGroup(projectId, securityGroupId) {
    try {
      const url = `openstack/${projectId}/security-groups/${securityGroupId}`;
      return await baseUrlWithAuth.delete(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getSingleSecurityGroups(projectId, securityGroupId) {
    try {
      const url = `openstack/${projectId}/security-groups/${securityGroupId}`;
      return await baseUrlWithAuth.get(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async deleteSecurityGroupRule(projectId, securityGroupRuleId) {
    try {
      const url = `openstack/${projectId}/security-group-rules/${securityGroupRuleId}`;
      return await baseUrlWithAuth.delete(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createAttachSecurityGroupInstance(data, projectId) {
    try {
      const url = `openstack/${projectId}/add-security-group`;
      return await baseUrlWithAuth.post(url, data);
    } catch (error) {
      throw error;
    }
  }

  static async removeSecurityGroupInstance(data, projectId) {
    try {
      const url = `openstack/${projectId}/remove-security-group`;
      return await baseUrlWithAuth.post(url, data);
    } catch (error) {
      throw error;
    }
  }

  static async getResourceStatus(serverId) {
    try {
      const url = `openstack/resources/${serverId}`;
      return await baseUrlWithAuth.get(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async deleteProject(projectId) {
    try {
      const url = `openstack/projects/${projectId}`;
      return await baseUrlWithAuth.delete(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async deleteServer(projectId, serverId) {
    try {
      const url = `openstack/${projectId}/servers/${serverId}`;
      return await baseUrlWithAuth.delete(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async deleteNetwork(projectId, networkId) {
    try {
      const url = `openstack/${projectId}/networks/${networkId}`;
      return await baseUrlWithAuth.delete(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async deleteRouter(projectId, routerId) {
    try {
      const url = `openstack/${projectId}/routers/${routerId}`;
      return await baseUrlWithAuth.delete(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async deleteNetworkSubnet(projectId, subnetId) {
    try {
      const url = `openstack/${projectId}/subnets/${subnetId}`;
      return await baseUrlWithAuth.delete(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async deleteLoadBalancer(projectId, loadBalancerId) {
    try {
      const url = `openstack/${projectId}/load-balancers/${loadBalancerId}?cascade=yes`;
      return await baseUrlWithAuth.delete(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async deleteLoadListener(projectId, listenerId) {
    try {
      const url = `openstack/${projectId}/listeners/${listenerId}`;
      return await baseUrlWithAuth.delete(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async deleteLoadHealthMonitor(projectId, healthMonitorId) {
    try {
      const url = `openstack/${projectId}/pool-health-monitors/${healthMonitorId}`;
      return await baseUrlWithAuth.delete(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async deleteMember(projectId, data) {
    try {
      const url = `openstack/${projectId}/pool-members/${data?.poolId}/${data?.memberId}`;
      return await baseUrlWithAuth.delete(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async deleteLoadPool(projectId, poolId) {
    try {
      const url = `openstack/${projectId}/load-balancer-pools/${poolId}`;
      return await baseUrlWithAuth.delete(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async deletePort(projectId, portId) {
    try {
      const url = `openstack/${projectId}/ports/${portId}`;
      return await baseUrlWithAuth.delete(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async deleteFloatIp(projectId, floatingIpId) {
    try {
      const url = `openstack/${projectId}/floating-ips/${floatingIpId}`;
      return await baseUrlWithAuth.delete(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async deleteInterface(projectId, data) {
    try {
      const url = `openstack/${projectId}/routers/${data.routerId}/interface`;
      return await baseUrlWithAuth.delete(url, { data });
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async stopServer(projectId, serverId) {
    try {
      const url = `openstack/${projectId}/servers/${serverId}/stop`;
      return await baseUrlWithAuth.get(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async restartServer(projectId, serverId) {
    try {
      const url = `openstack/${projectId}/servers/${serverId}/start`;
      return await baseUrlWithAuth.get(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async volumeList(projectId) {
    try {
      const url = `openstack/${projectId}/volumes`;
      return await baseUrlWithAuth.get(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async networkList(projectId) {
    try {
      const url = `openstack/${projectId}/networks`;
      return await baseUrlWithAuth.get(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async subnetList(projectId, params) {
    try {
      const url = `openstack/${projectId}/subnets${params}`;
      return await baseUrlWithAuth.get(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async listenerList(projectId, params) {
    try {
      const url = `openstack/${projectId}/listeners${params}`;
      return await baseUrlWithAuth.get(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async healthMonitorList(projectId, poolId) {
    try {
      const url = `openstack/${projectId}/pool-health-monitors?pool_id=${poolId}`;
      return await baseUrlWithAuth.get(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async memberList(projectId, poolId) {
    try {
      const url = `openstack/${projectId}/pool-members/${poolId}`;
      return await baseUrlWithAuth.get(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async poolList(projectId, params) {
    try {
      const url = `openstack/${projectId}/get-load-balancer-pools${params}`;
      return await baseUrlWithAuth.get(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async portList(projectId, params) {
    try {
      const url = `openstack/${projectId}/ports${params}`;
      return await baseUrlWithAuth.get(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async routerList(projectId) {
    try {
      const url = `openstack/${projectId}/routers`;
      return await baseUrlWithAuth.get(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async routerInterfaceList(projectId, data) {
    try {
      const url = `openstack/${projectId}/routers/${data?.routerId}/interface`;
      return await baseUrlWithAuth.get(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createSecret(projectId, secretData) {
    try {
      const url = `openstack/${projectId}/secrets/create-secret`;
      return await baseUrlWithAuth.post(url, secretData);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getSecrets(projectId) {
    try {
      const url = `openstack/${projectId}/secrets`;
      return await baseUrlWithAuth.get(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getSecretPayload(projectId, secretId) {
    try {
      const url = `openstack/${projectId}/secrets/${secretId}/payload`;
      return await baseUrlWithAuth.get(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async deleteSecret(projectId, secretId) {
    try {
      const url = `openstack/${projectId}/secrets/${secretId}`;
      return await baseUrlWithAuth.delete(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createSecretContainer(projectId, containerData) {
    try {
      const url = `openstack/${projectId}/secret-containers/create-secret-container`;
      return await baseUrlWithAuth.post(url, containerData);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getSecretContainers(projectId) {
    try {
      const url = `openstack/${projectId}/secret-containers`;
      return await baseUrlWithAuth.get(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getSecretContainer(projectId, containerId) {
    try {
      const url = `openstack/${projectId}/secret-containers/${containerId}`;
      return await baseUrlWithAuth.get(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async deleteSecretContainer(projectId, containerId) {
    try {
      const url = `openstack/${projectId}/secret-containers/${containerId}`;
      return await baseUrlWithAuth.delete(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async addSecretToContainer(projectId, containerId, data) {
    try {
      const url = `openstack/${projectId}/secret-containers/${containerId}/secrets`;
      return await baseUrlWithAuth.post(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async removeSecretFromContainer(projectId, containerId, data) {
    try {
      const url = `openstack/${projectId}/secret-containers/${containerId}/remove-secret`;
      return await baseUrlWithAuth.post(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }
  static async passwordResetLink(data) {
    try {
      return baseUrlNoAuth.post(`users/password-reset-email`, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }
  static async setNewPassword(data) {
    try {
      return baseUrlNoAuth.post(`/users/set-new-password`, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async updateProfile(data) {
    try {
      return baseUrlWithAuth.put(`/users/update-profile`, data);
    } catch (error) {
      throw error;
    }
  }

  static async createVolume(data, projectId) {
    try {
      const url = `openstack/${projectId}/volume-create`;
      return await baseUrlWithAuth.post(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createNetwork(data, projectId) {
    try {
      const url = `openstack/${projectId}/networks/create-network`;
      return await baseUrlWithAuth.post(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async allocateFloatingIPS(data, projectId) {
    try {
      const url = `openstack/${projectId}/floating-ips/create-floating-ip`;
      return await baseUrlWithAuth.post(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createNetworkPort(data, projectId) {
    try {
      const url = `openstack/${projectId}/ports/create-port`;
      return await baseUrlWithAuth.post(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createRouter(data, projectId) {
    try {
      const url = `openstack/${projectId}/routers/create-router`;
      return await baseUrlWithAuth.post(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async updateNetwork(data, projectId) {
    try {
      const url = `openstack/${projectId}/networks/${data?.networkId}`;
      return await baseUrlWithAuth.put(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async updateNetworkPort(data, projectId) {
    try {
      const url = `openstack/${projectId}/ports/${data?.portId}`;
      return await baseUrlWithAuth.put(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async updateFloatingIp(data, projectId) {
    try {
      const url = `openstack/${projectId}/floating-ips/${data?.floatingIpId}`;
      return await baseUrlWithAuth.put(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async updateSubnet(data, projectId) {
    try {
      const url = `openstack/${projectId}/subnets/${data?.subnetId}`;
      return await baseUrlWithAuth.put(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async updateSnapshot(data, projectId) {
    try {
      const url = `openstack/${projectId}/snapshots/${data?.snapshotId}`;
      return await baseUrlWithAuth.put(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async updateSnapshotForServer(data, projectId) {
    try {
      const url = `openstack/${projectId}/servers/snapshots/update-snapshot/${data?.snapshotId}`;
      return await baseUrlWithAuth.patch(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async updateRouter(data, projectId) {
    try {
      const url = `openstack/${projectId}/routers/${data?.routerId}`;
      return await baseUrlWithAuth.put(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async updateListener(data, projectId) {
    try {
      const url = `openstack/${projectId}/listeners/${data?.listenerId}`;
      return await baseUrlWithAuth.put(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async updateHealthMonitors(data, projectId) {
    try {
      const url = `openstack/${projectId}/pool-health-monitors/${data?.healthMonitorId}`;
      const dataToPass = {
        type: data?.type,
        delay: data?.delay,
        name: data?.name,
        max_retries: data?.max_retries,
        max_retries_down: data?.max_retries_down,
        timeout: data?.timeout,
        http_method: data?.http_method,
        expected_codes: data?.expected_codes,
        url_path: data?.url_path,
        http_version: data?.http_version,
      };
      return await baseUrlWithAuth.put(url, dataToPass);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async updateMember(data, projectId) {
    try {
      const url = `openstack/${projectId}/pool-members/${data?.poolId}/${data?.memberId}`;

      const editMemberFormData = {
        name: data?.name,
        weight: data?.weight,
        backup: data?.backup,
      };

      return await baseUrlWithAuth.put(url, editMemberFormData);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async updatePool(data, projectId) {
    try {
      const url = `openstack/${projectId}/load-balancer-pools/${data?.poolId}`;
      return await baseUrlWithAuth.put(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createSubnet(data, projectId) {
    try {
      const url = `openstack/${projectId}/networks/${data.networkId}/subnet`;
      return await baseUrlWithAuth.post(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createInterface(data, projectId) {
    try {
      const url = `openstack/${projectId}/routers/${data.routerId}/interface`;
      return await baseUrlWithAuth.post(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createLoadBalancers(data, projectId) {
    try {
      const url = `openstack/${projectId}/create-load-balancer`;
      return await baseUrlWithAuth.post(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createPool(data, projectId) {
    try {
      const url = `openstack/${projectId}/load-balancer-pools/create-load-balancer-pool`;
      return await baseUrlWithAuth.post(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createListeners(data, projectId) {
    try {
      const url = `openstack/${projectId}/listeners/create-listener`;
      return await baseUrlWithAuth.post(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createHealthMonitors(data, projectId) {
    try {
      const url = `openstack/${projectId}/create-pool-health-monitor`;
      return await baseUrlWithAuth.post(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createMember(data, projectId) {
    try {
      const url = `openstack/${projectId}/pool-members/create-pool-member`;
      return await baseUrlWithAuth.post(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getLoadBalancers(projectId) {
    try {
      return baseUrlWithAuth.get(`/openstack/${projectId}/load-balancers`);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  //This is where billing module start from --dynamite

  static async userResourceSubscription() {
    try {
      return baseUrlWithAuth.get('/openstack/billing/subscriptions');
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async creditHistory() {
    try {
      return baseUrlWithAuth.get('/openstack/billing/credit-history');
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async paymentHistory() {
    try {
      return baseUrlWithAuth.get('/openstack/billing/payment-history');
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getAuditLogs() {
    try {
      return baseUrlWithAuth.get('/openstack/audit-logs');
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async getInvoices() {
    try {
      return baseUrlWithAuth.get('/openstack/billing/get_invoice');
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async payInvoice(data) {
    try {
      return baseUrlWithAuth.post('/openstack/billing/invoices/pay', data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async attachInstance(data, projectId, serverId) {
    try {
      const url = `openstack/${projectId}/servers/${serverId}/attach-volume`;
      return await baseUrlWithAuth.post(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async restoreBackupFromVolume(data, projectId) {
    try {
      const url = `openstack/${projectId}/backups/${data?.backupId}`;
      return await baseUrlWithAuth.post(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async detachInstance(projectId, serverId, volumeId) {
    try {
      const url = `openstack/${projectId}/volumes/${serverId}/${volumeId}/detach-from-server`;
      return await baseUrlWithAuth.post(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async generatePaymentLink(data) {
    try {
      const url = `openstack/flutterwave/payment_link`;
      return await baseUrlWithAuth.post(url, data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async deleteVolumeFromInstance(projectId, volumeId) {
    try {
      const url = `openstack/${projectId}/volumes/${volumeId}?cascade=1`;
      return await baseUrlWithAuth.delete(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async deleteImageFromVolume(projectId, snapshotId) {
    try {
      const url = `openstack/${projectId}/server-snapshots/${snapshotId}`;
      return await baseUrlWithAuth.delete(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async deleteBackupFromVolume(projectId, backupId) {
    try {
      const url = `openstack/${projectId}/backups/${backupId}`;
      return await baseUrlWithAuth.delete(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async deleteSnapshot(projectId, snapshotId) {
    try {
      const url = `openstack/${projectId}/snapshots/${snapshotId}`;
      return await baseUrlWithAuth.delete(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async deleteServerSnapshot(projectId, snapshotId) {
    try {
      const url = `openstack/${projectId}/server-snapshots/${snapshotId}`;
      return await baseUrlWithAuth.delete(url);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async switchCurrency(data) {
    try {
      return baseUrlWithAuth.post('/openstack/update-deno', data);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async fetchServerMetrics(projectId, serverId, startDate, endDate) {
    try {
      return baseUrlWithAuth.get(
        `/openstack/${projectId}/servers/${serverId}/metric?start_date=${startDate}&end_date=${endDate}`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }
  static async fetchServerDiskMetrics(projectId, serverId, startDate, endDate) {
    try {
      return baseUrlWithAuth.get(
        `/openstack/${projectId}/servers/server-disk-report?server_id=${serverId}&start_date=${startDate}&end_date=${endDate}`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async fetchServerEgress(projectId, serverId) {
    try {
      return baseUrlWithAuth.get(
        `/openstack/${projectId}/servers/${serverId}/egress`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createBucketStorage(projectId, data) {
    try {
      return baseUrlWithAuth.post(
        `/openstack/${projectId}/bucket-storages/create-bucket-storage`,
        data,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async fetchBucketStorage(projectId) {
    try {
      return baseUrlWithAuth.get(`/openstack/${projectId}/bucket-storages`);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async updateBucketStorage(projectId, data) {
    try {
      return baseUrlWithAuth.put(
        `/openstack/${projectId}/bucket-storages/update-bucket-storage`,
        data,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }
  static async deleteBucketStorage(projectId, bucketName) {
    try {
      return baseUrlWithAuth.delete(
        `/openstack/${projectId}/bucket-storages/${bucketName}`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async uploadObject(projectId, bucketName, file) {
    try {
      return baseUrlWithAuth.post(
        `/openstack/${projectId}/bucket-storages/${bucketName}/upload-object`,
        file,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          maxBodyLength: Infinity,
          maxContentLength: Infinity,
          responseType: 'json',
        },
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }
  static async uploadObjects(projectId, bucketName, files) {
    try {
      return baseUrlWithAuth.post(
        `/openstack/${projectId}/bucket-storages/${bucketName}/upload-objects`,
        files,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          maxBodyLength: Infinity,
          maxContentLength: Infinity,
          responseType: 'json',
        },
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }
  static async deleteObject(projectId, bucketName, objectName) {
    try {
      return baseUrlWithAuth.delete(
        `/openstack/${projectId}/bucket-storages/${bucketName}/delete-object/${objectName}`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }
  static async fetchBucketStorageObjects(projectId, bucketName) {
    try {
      return baseUrlWithAuth.get(
        `/openstack/${projectId}/bucket-storages/${bucketName}`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }
  static async fetchObject(projectId, bucketName, objectName) {
    try {
      return baseUrlWithAuth.get(
        `/openstack/${projectId}/bucket-storages/${bucketName}/${objectName}/download`,
        { responseType: 'blob' },
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }
  static async previewObject(projectId, bucketName, objectName) {
    try {
      return baseUrlWithAuth.get(
        `/openstack/${projectId}/bucket-storages/${bucketName}/${objectName}/stream`,
        { responseType: 'blob' },
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }
  static async createFolder(projectId, bucketName, folderName) {
    try {
      return baseUrlWithAuth.post(
        `/openstack/${projectId}/bucket-storages/${bucketName}/create-folder/${folderName}`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }
  static async addFileToFolder(projectId, bucketName, folderName, file) {
    try {
      return baseUrlWithAuth.post(
        `/openstack/${projectId}/bucket-storages/${bucketName}/upload-object-folder/${folderName}`,
        file,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          maxBodyLength: Infinity,
          maxContentLength: Infinity,
          responseType: 'json',
        },
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }
  static async deleteFolder(projectId, bucketName, folderName) {
    try {
      return baseUrlWithAuth.delete(
        `/openstack/${projectId}/bucket-storages/${bucketName}/delete-folder/${folderName}`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }
  static async deleteFileFromFolder(
    projectId,
    bucketName,
    folderName,
    objectName,
  ) {
    try {
      return baseUrlWithAuth.delete(
        `/openstack/${projectId}/bucket-storages/${bucketName}/delete-object/${folderName}/${objectName}`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createServerAlarming(projectId, serverId, data) {
    try {
      return baseUrlWithAuth.post(
        `/openstack/${projectId}/alarming/${serverId}`,
        data,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async updateServerAlarming(projectId, serverId, data) {
    try {
      return baseUrlWithAuth.patch(
        `/openstack/${projectId}/alarming/${serverId}`,
        data,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }
  static async getServerAlarmingDetails(projectId, serverId) {
    try {
      return baseUrlWithAuth.get(
        `/openstack/${projectId}/alarming/${serverId}`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }
  static async deleteServerAlarming(projectId, serverId) {
    try {
      return baseUrlWithAuth.delete(
        `/openstack/${projectId}/alarming/${serverId}`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async createStack(projectId, data) {
    try {
      return baseUrlWithAuth.post(
        `/openstack/orchas/stacks/${projectId}/create-stack`,
        data,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async listStacks(projectId) {
    try {
      return baseUrlWithAuth.get(`/openstack/orchas/stacks/${projectId}`);
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }
  static async getStack(projectId, stackId, stackName) {
    try {
      return baseUrlWithAuth.get(
        `/openstack/orchas/stacks/${projectId}/${stackId}/${stackName}`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async updateStack(projectId, stackId, stackName, data) {
    try {
      return baseUrlWithAuth.put(
        `/openstack/orchas/stacks/${projectId}/${stackId}/${stackName}`,
        data,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }

  static async deleteStack(projectId, stackId, stackName) {
    try {
      return baseUrlWithAuth.delete(
        `/openstack/orchas/stacks/${projectId}/${stackId}/${stackName}`,
      );
    } catch (error) {
      apiEndPoints.extractError(error);
      throw error;
    }
  }
}
